import React, {MouseEvent, ReactNode} from "react";
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, useTheme} from "@mui/material";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {XIcon} from "@/agentur/AgenturIcons";

/**
 * Props for the ConfirmationDialog component.
 *
 * @property {boolean} open - If true, the dialog is open.
 * @property {() => void} onClose - The function to call when the dialog is closed.
 * @property {(e?: MouseEvent<HTMLButtonElement>) => void | Promise<void>} onConfirm - The function to call when the confirm button is clicked.
 * @property {ReactNode} title - The title of the dialog.
 * @property {ReactNode} message - The message to display in the dialog.
 * @property {string} confirmButtonText - The text to display on the confirm button.
 * @property {string} cancelButtonText - The text to display on the cancel button.
 * @property {"xs" | "sm" | "md" | "lg" | "xl"} [maxWidth] - The maximum width of the dialog.
 * @property {boolean} [fullWidth] - If true, the dialog will take up the full width of its container.
 * @property {string} [dataCy] - The data-cy attribute for the dialog.
 */
interface ConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (e?: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
    title: ReactNode;
    message: ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
    fullWidth?: boolean;
    dataCy?: string;
    closeOnConfirm?: boolean;
    children?: ReactNode;
    confirmDisabled?: boolean;
    autoFocusConfirm?: boolean;
}

/**
 * ConfirmationDialog component.
 *
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmButtonText,
    cancelButtonText,
    maxWidth = "sm",
    fullWidth = true,
    dataCy,
    closeOnConfirm,
    children,
    confirmDisabled,
    autoFocusConfirm = true
}) => {
    const theme = useTheme();
    return (
        <Dialog
            data-cy={dataCy ?? "confirmation-dialog"}
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">{message}</DialogContentText>
                {children}
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "flex-end", padding: 2, gap: 2 }}>
                {cancelButtonText && (
                    <ButtonEx
                        label={cancelButtonText}
                        startIcon={<XIcon mode={theme.palette.mode} />}
                        data-cy="cancel-button"
                        onClick={onClose}
                        color="success"
                    />
                )}
                <ButtonEx
                    label={confirmButtonText}
                    data-cy="confirm-button"
                    color="warning"
                    onClick={(e) => {
                        if (closeOnConfirm) {
                            onClose();
                        }
                        onConfirm(e);
                    }}
                    disabled={!!confirmDisabled}
                    shouldFocus={autoFocusConfirm}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
